import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Database, Cpu, BrainCircuit, Workflow, TrendingUp } from "lucide-react";
import SectionHeading from "../common/SectionHeading";

const STAGES = [
  { k: "01", tag: "Data", title: "Ingest & Unify", icon: Database, desc: "Connect siloed sources into clean, governed, analysis-ready data.", accent: "#22D3EE" },
  { k: "02", tag: "Processing", title: "Engineer & Pipeline", icon: Cpu, desc: "Scalable real-time pipelines transform raw data into trusted signals.", accent: "#2563EB" },
  { k: "03", tag: "Intelligence", title: "Model & Predict", icon: BrainCircuit, desc: "AI and ML models surface patterns, forecasts and recommendations.", accent: "#22D3EE" },
  { k: "04", tag: "Decision", title: "Act & Automate", icon: Workflow, desc: "Insights drive automated, confident decisions across the business.", accent: "#2563EB" },
  { k: "05", tag: "Business Outcome", title: "Grow & Scale", icon: TrendingUp, desc: "Measurable outcomes and a durable, compounding competitive edge.", accent: "#D4AF37" },
];

function Panel({ stage, index }) {
  const Ic = stage.icon;
  return (
    <div
      className="relative flex h-full w-[85vw] shrink-0 flex-col justify-between rounded-3xl border border-white/10 bg-ink-800/50 p-8 sm:w-[70vw] lg:w-[42vw] lg:p-10"
      data-testid={`dataflow-panel-${index}`}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 rounded-full opacity-40 blur-2xl"
        style={{ background: `radial-gradient(circle, ${stage.accent}55, transparent 70%)` }}
      />
      <div className="flex items-start justify-between">
        <span
          className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5"
          style={{ color: stage.accent }}
        >
          <Ic className="h-6 w-6" />
        </span>
        <span className="font-heading text-5xl font-bold text-white/10 lg:text-6xl">{stage.k}</span>
      </div>
      <div className="mt-10">
        <div className="text-xs font-medium uppercase tracking-[0.28em]" style={{ color: stage.accent }}>
          {stage.tag}
        </div>
        <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white lg:text-3xl">
          {stage.title}
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400 lg:text-base">{stage.desc}</p>
      </div>
    </div>
  );
}

export default function DataFlowStory() {
  const sectionRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const prog = useMotionValue(0);

  useEffect(() => {
    const decide = () => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setPinned(!reduced && window.innerWidth >= 1024);
    };
    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, []);

  // Drive progress from real window scroll (reliable with Lenis smooth-scroll,
  // unlike framer useScroll which under-reports in this layout).
  useEffect(() => {
    if (!pinned) return;
    let raf = 0;
    const update = () => {
      const el = sectionRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const total = el.offsetHeight - window.innerHeight;
      const p = total > 0 ? (window.scrollY - top) / total : 0;
      prog.set(Math.min(1, Math.max(0, p)));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [pinned, prog]);

  const x = useTransform(prog, [0, 1], ["2vw", "-124vw"]);
  const progress = useTransform(prog, [0, 1], ["0%", "100%"]);

  // Fallback: vertical stack on mobile / reduced-motion
  if (!pinned) {
    return (
      <section className="px-6 py-24 lg:py-32" data-testid="dataflow-section">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="How We Deliver"
            title="From Data to Business Outcome"
            subtitle="A continuous flow that turns raw data into intelligent, automated decisions — and measurable results."
            testId="dataflow"
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {STAGES.map((s, i) => (
              <Panel key={s.k} stage={s} index={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative h-[320vh]" data-testid="dataflow-section">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-7xl px-6">
          <SectionHeading
            eyebrow="How We Deliver"
            title="From Data to Business Outcome"
            subtitle="Scroll to follow the flow — raw data becomes intelligent, automated decisions and measurable results."
            testId="dataflow"
          />
        </div>
        <motion.div
          style={{ x }}
          className="mt-12 flex gap-6 pl-6 lg:pl-[max(1.5rem,calc((100vw-80rem)/2))]"
        >
          {STAGES.map((s, i) => (
            <Panel key={s.k} stage={s} index={i} />
          ))}
          <div className="w-[12vw] shrink-0" aria-hidden="true" />
        </motion.div>
        <div className="mx-auto mt-12 w-full max-w-7xl px-6">
          <div className="h-px w-full bg-white/10">
            <motion.div style={{ width: progress }} className="h-px bg-gradient-to-r from-sky-400 to-cyan-300" />
          </div>
        </div>
      </div>
    </section>
  );
}
